import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AuthConfigStore,
  ConfigService,
  OidcConfiguration,
  Plant,
  TokenRefreshResponse,
} from '@orion-platform/core';
import {
  AuthenticatedResult,
  AuthStateResult,
  EventTypes,
  LoginResponse,
  OidcSecurityService,
  PublicEventsService,
  ValidationResult,
} from 'angular-auth-oidc-client';
import { SetDefaultDashboardSettings } from 'apps/orion/src/app/features/scm/material-management/v1/store/supply-visibility/supply-visibility.actions';
import { UserRoles } from 'apps/orion/src/app/features/scm/material-management/v1/types';
import { detectIncognito } from 'detectincognitojs';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, take, tap, timeout } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { Helper } from '../../../../../apps/orion/src/app/helper/helper';
import { environment } from '../../../../../apps/orion/src/environments/environment';
import { LoggingService } from '../errors/Logging.service';
import { serverErrorInterceptorState } from '../interceptors/server-error.interceptor';
import { SignalRService } from '../services/signal-r.service';
import {
  AuthenticatedUser,
  EnumAuthFlags,
  EnumSharedSessionEvents,
  SessionExchangeFormat,
  TabPayload,
} from './authenticated-user.model';
import { LoginAdfsSuccess, SetUserRightsByRole } from './authentication.actions';

const authUrlPaths = {
  LOGIN: 'auth/login',
  LOGOUT: 'auth/logout',
};

const PLANT_WAIT_TIMEOUT_MS = 30000;

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  // Public API
  isPrivateMode = false;
  idToken = '';

  // Configuration / constants
  private readonly authResultKeySuffix = '_authnResult';
  private readonly CACHED_FLAG_VALUE = 'cache';
  private ignoreUrls: string[] = [];
  private readonly readOtherTabsTimeoutMilliseconds = 1000;
  private readonly tabId: string = uuidv4();
  private readonly tokenExpireOffsetInSeconds = environment.tokenConfig.tokenExpireOffsetInSeconds;

  // State flags
  private configLoadedOnce = false;
  private hasStorage!: boolean;
  readonly isAuthenticated = signal(false); // NOTE it will init on OIDC Config Load;
  private initialSetupDone = false;
  private isRefreshingSession = false;
  private sharedSessionStatus: 'Idle' | 'Waiting' = 'Idle';
  private writeLogs = true; // NOTE Turn on for debug login flow log
  private oauthClientSecret = '';
  private oauthClientId = '';

  // Derived / runtime numeric state
  private renewTokenExpireTimeInMilliseconds!: number;

  // Timeout / handler references
  private checkAuthTimeoutHandler?: ReturnType<typeof setTimeout>;
  private expiredSessionHandler?: ReturnType<typeof setTimeout>;
  private refreshSessionHandler?: ReturnType<typeof setTimeout>;

  // Angular Dependency injections
  private readonly authConfigStore = inject(AuthConfigStore);
  private readonly configService = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly eventService = inject(PublicEventsService);
  private readonly httpClient = inject(HttpClient);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly signalRService = inject(SignalRService);
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);
  private readonly loggingService = inject(LoggingService);

  constructor() {
    this.checkIncognitoMode();

    //Required to work with session/local storage
    this.hasStorage = typeof Storage !== 'undefined';
    if (this.hasStorage) {
      if (this.writeLogs) {
        this.writeLog('[01-024] Has Permission of Session/Local Storage');
      }
    } else {
      if (this.writeLogs) {
        this.writeLog('[01-025] Does not have Permission of Session/Local Storage');
      }
    }

    sessionStorage.removeItem(EnumAuthFlags.OidcInitiated);

    //Attaching Listener to listen other orion  tabs and other applications
    this.localStorageListener();

    //Subscribing Oidc Lib Events (Authentication Events)
    this.subscribeOidcEvents();

    this.oidcSecurityService.isAuthenticated$.subscribe((authenticatedResult): void => {
      this.isAuthenticated.set(authenticatedResult?.isAuthenticated ?? false);
    });

    if (this.writeLogs) {
      this.writeLog('[01-023] Constructor for authentication is completed');
    }
  }

  private async checkIncognitoMode(): Promise<void> {
    try {
      const browserInfo = await detectIncognito();
      this.isPrivateMode = browserInfo.isPrivate;

      if (this.writeLogs) {
        this.writeLog(
          this.isPrivateMode
            ? '[01-021] Incognito Mode is detected'
            : '[01-022] Incognito Mode is not detected',
        );
      }
    } catch (error) {
      console.error('Private mode error:', error);
      if (this.writeLogs) {
        this.writeLog('[01-023] Error while detecting incognito mode');
      }
    }
  }

  private cancellationToken$ = new Subject<void>();
  private isNewLogin$ = new BehaviorSubject<boolean>(false);
  private isCancellationTokenEnable = true;
  private activePlantsCache$?: Observable<Plant[]>;

  async getActivePlants(): Promise<Plant[]> {
    if (!this.activePlantsCache$) {
      const apiUrl = this.configService.getSettings('userSettingsService');
      this.activePlantsCache$ = this.httpClient
        .get<Plant[]>(`${apiUrl}${environment.activePlants.getactiveplants}`)
        .pipe(shareReplay(1));
    }
    try {
      let result = await firstValueFrom(this.activePlantsCache$);
      // BE occasionally returns empty/undefined on re-login when the GET fires before the new
      // JWT is wired into the HTTP interceptor. Retry once after a short delay so the new
      // user's plants list actually gets replaced (not left empty from LoginAdfsSuccess clear).
      if (!Array.isArray(result) || result.length === 0) {
        this.resetPlantsCache();
        await new Promise<void>((resolve): void => {
          setTimeout(resolve, 1000);
        });
        const apiUrl = this.configService.getSettings('userSettingsService');
        this.activePlantsCache$ = this.httpClient
          .get<Plant[]>(`${apiUrl}${environment.activePlants.getactiveplants}`)
          .pipe(shareReplay(1));
        result = await firstValueFrom(this.activePlantsCache$);
      }
      return result;
    } catch (error) {
      this.resetPlantsCache();
      throw error;
    }
  }

  resetPlantsCache(): void {
    this.activePlantsCache$ = undefined;
  }

  public snapshotForDiagnostics(label: string, extra?: Record<string, unknown>): void {
    try {
      const clientId = this.oauthClientId;
      const authnKey = this.findAuthResultKey() || `${clientId}_authnResult`;
      const authnRaw = sessionStorage.getItem(authnKey);
      let authn: null | TokenRefreshResponse = null;
      try {
        authn = authnRaw ? JSON.parse(authnRaw) : null;
      } catch {}
      const accessExpRaw = sessionStorage.getItem(`${clientId}_access_token_expires_at`);
      const accessExp = accessExpRaw ? parseInt(accessExpRaw, 10) : null;
      const wellknownRaw = sessionStorage.getItem(`${clientId}_authWellKnownEndPoints`);
      let wellknown: null | { tokenEndpoint?: string } = null;
      try {
        wellknown = wellknownRaw ? JSON.parse(wellknownRaw) : null;
      } catch {}

      const snap = this.store.snapshot() || {};
      const auth = (snap && snap.authentication) || {};
      const plantCode = typeof auth.plant === 'object' && auth.plant ? auth.plant.Code : auth.plant;

      const now = Date.now();

      const payload = {
        context: {
          isAuthenticated: this.isAuthenticated(),
          isRefreshingSession: this.isRefreshingSession,
          pageLifetimeMs: typeof performance !== 'undefined' ? Math.round(performance.now()) : null,
          path: this.getCurrentPath().currentPath,
          visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
        },
        extra: extra || null,
        keycloak: {
          accessTokenExpiresAt: accessExp,
          accessTokenExpiresInMs: accessExp ? accessExp - now : null,
          accessTokenLength: authn && authn.access_token ? authn.access_token.length : 0,
          clientId,
          expiresIn: authn ? authn.expires_in : null,
          hasAccessToken: !!(authn && authn.access_token),
          hasRefreshToken: !!(authn && authn.refresh_token),
          refreshExpiresIn: authn ? authn.refresh_expires_in : null,
          refreshTokenLength: authn && authn.refresh_token ? authn.refresh_token.length : 0,
          tokenEndpoint: wellknown ? wellknown.tokenEndpoint : null,
        },
        label,
        ngxs: {
          currentRole: auth.currentRole,
          currentRolesCount: Array.isArray(auth.currentRoles) ? auth.currentRoles.length : null,
          hasToken: !!auth.token,
          isLoggedIn: auth.isLoggedIn,
          plant: plantCode,
          plantsCount: Array.isArray(auth.plants) ? auth.plants.length : null,
          plantsFirstFew: Array.isArray(auth.plants)
            ? auth.plants
                .slice(0, 5)
                .map(
                  (p: { Code?: string; Show?: boolean }): { Code?: string; Show?: boolean } =>
                    p && { Code: p.Code, Show: p.Show },
                )
            : null,
          plantsKind: Array.isArray(auth.plants) ? 'array' : typeof auth.plants,
          username: auth.username,
        },
        now,
        timestamp: new Date().toISOString(),
      };

      console.log(`[DIAG-REFRESH-SESSION] ${label}`, payload);

      this.loggingService
        .logErrorToApi({
          columnNumber: 0,
          error: JSON.stringify(payload),
          lineNumber: 0,
          message: `[DIAG-REFRESH-SESSION] ${label}`,
          source: 'authentication.service.ts',
        })
        .subscribe();
    } catch (e) {
      console.warn('[DIAG-REFRESH-SESSION] snapshot failed', e);
    }
  }

  private logSessionRefreshEvent(event: string, data: Record<string, unknown>): void {
    try {
      let ngxs: Record<string, unknown>;
      try {
        const snap = this.store.snapshot() || {};
        const auth = (snap && snap.authentication) || {};
        const plantCode =
          typeof auth.plant === 'object' && auth.plant ? auth.plant.Code : auth.plant;
        ngxs = {
          currentRole: auth.currentRole,
          currentRolesCount: Array.isArray(auth.currentRoles) ? auth.currentRoles.length : null,
          hasToken: !!auth.token,
          isLoggedIn: auth.isLoggedIn,
          plant: plantCode,
          plantsCount: Array.isArray(auth.plants) ? auth.plants.length : null,
          username: auth.username,
        };
      } catch (e) {
        ngxs = { snapshotError: String(e) };
      }

      const payload = {
        context: {
          isAuthenticated: this.isAuthenticated(),
          isRefreshingSession: this.isRefreshingSession,
          pageLifetimeMs: typeof performance !== 'undefined' ? Math.round(performance.now()) : null,
          path: this.getCurrentPath().currentPath,
          visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
        },
        event,
        ngxs,
        timestamp: new Date().toISOString(),
        ...data,
      };
      console.log(`[DIAG-REFRESH-SESSION] ${event}`, payload);
      this.loggingService
        .logErrorToApi({
          columnNumber: 0,
          error: JSON.stringify(payload),
          lineNumber: 0,
          message: `[DIAG-REFRESH-SESSION] ${event}`,
          source: 'authentication.service.ts',
        })
        .subscribe();
    } catch (e) {
      console.warn('[DIAG-REFRESH-SESSION] log failed', e);
    }
  }

  checkAuthOnce(): Observable<LoginResponse> {
    return this.oidcSecurityService.checkAuth().pipe(
      tap((response): void => {
        if (response && response.isAuthenticated) {
          this.setSignalR();
        }
      }),
    );
  }

  invokeCancellationToken(): void {
    this.cancellationToken$.next();
  }

  getCancellationTokenObservable(): Observable<void> {
    return this.cancellationToken$.asObservable();
  }

  setLoginStatus(isLogin: boolean): void {
    this.isNewLogin$.next(isLogin);
  }

  getLoginStatus(): Observable<boolean> {
    return this.isNewLogin$.asObservable();
  }

  setCancellationToken(event: boolean, calledFor?: string): void {
    this.writeLog(
      `[01-048] Cancellation token ${event == true ? 'enabled' : 'disabled'} for ${calledFor}`,
    );
    this.isCancellationTokenEnable = event;
  }

  getCancellationToken(): boolean {
    return this.isCancellationTokenEnable;
  }

  public getClientSecret(): string {
    return this.oauthClientSecret;
  }

  /**
   * Directly calls the Keycloak token endpoint with the refresh token.
   * Bypasses the OIDC library's internal guards (isSilentRenewRunning, areAuthStorageTokensValid).
   * Required because the library returns stale tokens as "success" when our 30s offset
   * triggers before the library considers the token expired.
   */
  public async performDirectTokenRefresh(): Promise<boolean> {
    try {
      const authResult = this.getAuthResult();
      if (!authResult || !authResult?.refresh_token) {
        this.snapshotForDiagnostics('direct-refresh:no-refresh-token', {
          authResultKeys: authResult ? Object.keys(authResult) : null,
          hasAuthResult: !!authResult,
        });
        this.writeLog('[direct-refresh] No refresh token available');
        return false;
      }

      const clientId = this.oauthClientId;
      if (!clientId) {
        this.snapshotForDiagnostics('direct-refresh:no-client-id');
        this.writeLog('[direct-refresh] No client ID configured');
        return false;
      }

      // Read token endpoint from the library's stored wellknown endpoints
      const wellknownKey = `${clientId}_authWellKnownEndPoints`;
      const wellknownRaw = sessionStorage.getItem(wellknownKey);
      if (!wellknownRaw) {
        this.snapshotForDiagnostics('direct-refresh:no-wellknown', { wellknownKey });
        this.writeLog('[direct-refresh] No wellknown endpoints in storage');
        return false;
      }
      const wellknown = JSON.parse(wellknownRaw);
      const tokenEndpoint = wellknown.tokenEndpoint;
      if (!tokenEndpoint) {
        this.snapshotForDiagnostics('direct-refresh:no-token-endpoint', {
          wellknownKeys: Object.keys(wellknown),
        });
        this.writeLog('[direct-refresh] No token endpoint in wellknown');
        return false;
      }

      let body = `grant_type=refresh_token&client_id=${encodeURIComponent(
        clientId,
      )}&refresh_token=${encodeURIComponent(authResult.refresh_token)}`;
      if (this.oauthClientSecret) {
        body += `&client_secret=${encodeURIComponent(this.oauthClientSecret)}`;
      }

      this.snapshotForDiagnostics('direct-refresh:posting', {
        hasClientSecret: !!this.oauthClientSecret,
        refreshTokenLength: authResult.refresh_token.length,
        tokenEndpoint,
      });

      const newTokens = await firstValueFrom(
        this.httpClient.post<TokenRefreshResponse>(tokenEndpoint, body, {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        }),
      );

      if (!newTokens || !newTokens.access_token) {
        this.snapshotForDiagnostics('direct-refresh:invalid-response', {
          hasNewTokens: !!newTokens,
          responseKeys: newTokens ? Object.keys(newTokens) : null,
        });
        this.writeLog('[direct-refresh] Invalid response from token endpoint');
        return false;
      }

      // Write new tokens back into the library's sessionStorage slots.
      const authnResultKey = this.findAuthResultKey() || `${clientId}_authnResult`;
      sessionStorage.setItem(authnResultKey, JSON.stringify(newTokens));
      sessionStorage.setItem(`${clientId}_authzData`, JSON.stringify(newTokens.access_token));
      if (newTokens.expires_in) {
        const expiresAt =
          new Date(new Date().toUTCString()).valueOf() + newTokens.expires_in * 1000;
        sessionStorage.setItem(`${clientId}_access_token_expires_at`, JSON.stringify(expiresAt));
      }

      const newAny = newTokens;
      this.snapshotForDiagnostics('direct-refresh:success', {
        accessTokenLength: newTokens.access_token ? newTokens.access_token.length : 0,
        expiresIn: newTokens.expires_in,
        newRefreshTokenLength: newAny.refresh_token ? newAny.refresh_token.length : 0,
        refreshExpiresIn: newAny.refresh_expires_in,
        rotatedRefreshToken:
          !!newAny.refresh_token && newAny.refresh_token !== authResult.refresh_token,
      });
      this.writeLog('[direct-refresh] Token refreshed successfully');
      return true;
    } catch (e) {
      const httpErr = e as HttpErrorResponse;
      this.snapshotForDiagnostics('direct-refresh:http-error', {
        errorBody: httpErr && httpErr.error,
        message: httpErr && httpErr.message,
        name: httpErr && httpErr.name,
        status: httpErr && httpErr.status,
        statusText: httpErr && httpErr.statusText,
        url: httpErr && httpErr.url,
      });
      this.writeLog('[direct-refresh] Error during token refresh', e);
      return false;
    }
  }

  public isAuthInitialized(): boolean {
    return !!sessionStorage.getItem(EnumAuthFlags.IsAuthFinished);
  }

  loginAdfs(): void {
    this.store.dispatch(new SetDefaultDashboardSettings(true, 'isLogin'));
    localStorage.setItem('isFirstLogin', 'true');
    this.oidcSecurityService.authorize();
  }

  /**
   * Stores the URL so we can redirect after signing in.
   */
  getRedirectUrl(): null | string {
    return this.hasStorage ? sessionStorage.getItem('redirectUrl') : null;
  }

  setRedirectUrl(url: string): void {
    if (this.hasStorage) {
      sessionStorage.setItem('redirectUrl', url);
    }
  }

  removeRedirectUrl(): void {
    sessionStorage.removeItem('redirectUrl');
  }

  async getUserName(): Promise<string | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      const { email, name, upn } = payload || {};
      const emailSplit = email?.split('@') || [];
      const username = name ?? emailSplit.shift();
      return username || email || upn;
    } catch {
      return undefined;
    }
  }

  async getEmailUserName(): Promise<string | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      const emailSplit = payload?.email?.split('@') || [];
      return emailSplit.shift();
    } catch {
      return undefined;
    }
  }

  async getUpn(): Promise<string | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      const { email, upn } = payload || {};
      return email || upn;
    } catch {
      return undefined;
    }
  }

  async getAuthTime(): Promise<number | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      return payload?.auth_time;
    } catch {
      return undefined;
    }
  }

  async getIdTokenExpireTime(): Promise<number | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      const exp = isNaN(payload?.exp) ? 0 : +payload.exp * 1000;
      return exp;
    } catch {
      return;
    }
  }

  async getGroupNames(): Promise<UserRoles[]> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      const groups = payload?.GroupsName;
      return Array.isArray(groups) ? groups : [];
    } catch {
      return [];
    }
  }

  async getPrimarySid(): Promise<string | undefined> {
    try {
      const payload = await firstValueFrom(this.oidcSecurityService.getPayloadFromIdToken());
      return payload?.primarysid;
    } catch {
      return undefined;
    }
  }

  getIsAuthorized(): Observable<AuthenticatedResult> {
    return this.oidcSecurityService.isAuthenticated$;
  }

  _getIsAuthorized(): boolean {
    return this.isAuthenticated();
  }

  getIdToken(): Observable<string> {
    return this.oidcSecurityService.getIdToken();
  }

  getToken(): Observable<null | string> {
    return this.oidcSecurityService.getAccessToken();
  }

  getPayloadFromIdToken(): Observable<unknown> {
    return this.oidcSecurityService.getPayloadFromIdToken();
  }

  async refreshTokens(): Promise<void> {
    this.isRefreshingSession = true;

    try {
      const { currentPath } = this.getCurrentPath();
      this.setRedirectUrl(currentPath);

      let response: null | unknown = null;

      try {
        response = await this.performDirectTokenRefresh();
      } catch (error) {
        if (this.writeLogs) {
          this.writeLog('[01-047] Orion Auth Server - Failed to refresh token by server', error);
        }
      }

      if (response) {
        setTimeout((): void => {
          this.store.dispatch(new SetUserRightsByRole());
          this.resetRefreshSessionVars();
          this.setRefreshSessionHandler();
          if (sessionStorage.length) {
            this.sendLocalStorageEvent(EnumSharedSessionEvents.RenewToken, sessionStorage);
          }
          this.writeLog('[01-017] Token was successfully refreshed.');
        }, 500);
      } else {
        // ORB-6875: Clear the in-progress flag BEFORE the expiry check: if the refresh token is
        // dead, checkIfRefreshTokenExpired triggers reAuthenticate, which bails out while a
        // refresh is still marked in progress.
        this.isRefreshingSession = false;
        if (this.checkIfRefreshTokenExpired(this.getCurrentTime())) {
          return;
        }

        // držet session živou když refresh token není expired
        this.setRefreshSessionHandler(true);
        if (this.writeLogs) {
          this.writeLog('[01-018] Refreshing Tokens Failed', response);
        }
      }
    } catch (error) {
      // ORB-6875: same ordering fix as above for the catch path.
      this.isRefreshingSession = false;
      if (this.checkIfRefreshTokenExpired(this.getCurrentTime())) {
        return;
      }

      this.setRefreshSessionHandler(true);
      if (this.writeLogs) {
        this.writeLog('forceRefreshSession-failed', error);
      }
    } finally {
      this.isRefreshingSession = false;
    }
  }

  async logoutAdfs(): Promise<void> {
    // Capture token before sessionStorage is cleared
    this.idToken = await firstValueFrom(this.getIdToken());
    localStorage.setItem('lastUrlBeforeLogout', window.location.href);
    localStorage.setItem(EnumAuthFlags.IsLoggedOffLocal, this.CACHED_FLAG_VALUE);
    this.sendLocalStorageEvent(EnumSharedSessionEvents.Logout);
    if (this.writeLogs) this.writeLog('logoutAdfs');
    if (this.refreshSessionHandler) clearTimeout(this.refreshSessionHandler);
    if (this.expiredSessionHandler) clearTimeout(this.expiredSessionHandler);
    sessionStorage.clear();
    console.log('[Dialog Testing] logout calling from logoutAdfs');
    this.logoutHandler();
  }

  loginApplication(credentials: {
    password: string;
    username: string;
  }): Observable<AuthenticatedUser> {
    const apiBaseUrl = environment.login.apiLoginUrl;
    return this.httpClient.post<AuthenticatedUser>(
      `${apiBaseUrl}${authUrlPaths.LOGIN}`,
      credentials,
    );
  }

  logout(): Observable<unknown> {
    const apiBaseUrl = environment.login.apiLoginUrl;
    return this.httpClient.post(`${apiBaseUrl}${authUrlPaths.LOGOUT}`, {});
  }

  /**
   * Logs in a user via domain authentication.
   * @param domain - Selected domain by user
   * @param username - User login name
   * @param password - User password
   * @param plant - Selected plant by user
   * @returns Observable of login response
   */
  loginApplicationDomain(credentials: {
    domain: string;
    password: string;
    plant: string;
    username: string;
  }): Observable<AuthenticatedUser> {
    const apiBaseUrl = environment.login.apiLoginUrl;
    return this.httpClient.post<AuthenticatedUser>(`${apiBaseUrl}`, credentials);
  }

  /**
   * Logs in user via Active Directory.
   * @param data - User login data (username, password, domain, etc.)
   * @returns Observable of login response
   */
  activeDirectory(data: Record<string, unknown>): Observable<unknown> {
    const apiBaseUrl = environment.login.apiLoginUrl;
    return this.httpClient.post(`${apiBaseUrl}`, data);
  }

  /**
   * Logs out the user authenticated via domain.
   * @returns Observable of logout response
   */
  logoutDomain(): Observable<unknown> {
    const apiDomainLogautURL = environment.login.apiLogoutUrl;
    return this.httpClient.get(`${apiDomainLogautURL}`);
  }

  /**
   * Fetches available plants from configuration.
   * @returns List of plants
   */
  fetchPlants(): string[] {
    return environment.modulesBaseUrl.plants;
  }

  private subscribeOidcEvents(): void {
    this.eventService
      .registerForEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (err): void => {
          if (this.writeLogs) this.writeLog('[OIDC Event Error]', err);
        },
        next: (notification): void => {
          switch (notification.type) {
            case EventTypes.CheckSessionReceived:
              this.onCheckSessionReceived(notification.value);
              break;
            case EventTypes.ConfigLoaded:
              this.onConfigLoaded(notification.value);
              break;
            case EventTypes.IdTokenExpired:
              this.onIdTokenExpired(notification.value);
              break;
            case EventTypes.NewAuthenticationResult:
              this.onNewAuthorizationResult(notification.value);
              break;
            case EventTypes.TokenExpired:
              this.onTokenExpired(notification.value);
              break;
            case EventTypes.UserDataChanged:
              this.onUserDataChanged(notification.value);
              break;
          }
        },
      });
  }

  private onCheckSessionReceived(data: unknown): void {
    if (this.writeLogs) this.writeLog('onCheckSessionReceived', data);
  }

  private onConfigLoaded(data: OidcConfiguration): void {
    if (this.configLoadedOnce) {
      return;
    }
    this.configLoadedOnce = true;
    this.sharedSessionStatus = 'Waiting';
    sessionStorage.setItem(EnumAuthFlags.OidcInitiated, this.CACHED_FLAG_VALUE);
    this.sendLocalStorageEvent(EnumSharedSessionEvents.Request);

    const wellknown = data?.authWellknownEndpoints;
    this.ignoreUrls = [wellknown.jwksUri, wellknown.authorizationEndpoint, wellknown.tokenEndpoint];

    // The effective settings, not the compiled ones: the client the app
    // presents at the token endpoint is the environment's, not dev's.
    const config = this.authConfigStore.current();
    this.oauthClientSecret = config && config.clientSecret ? config.clientSecret : '';
    this.oauthClientId = config && config.clientId ? config.clientId : '';

    if (!localStorage.getItem(EnumAuthFlags.IsLoggedOffLocal)) {
      if (this.writeLogs) this.writeLog('[01-031] LogOffLocal - False');

      this.checkAuthTimeoutHandler = setTimeout((): void => {
        if (this.writeLogs) this.writeLog('[01-032] Timeout of reading other orion tabs');
        this.sharedSessionStatus = 'Idle';
        // Auth check is handled by app.ts checkAuthOnce() – do NOT call
        // checkAuth(true) here, as it causes a duplicate token endpoint
        // call resulting in 400 Bad Request (authorization code already consumed).
      }, this.readOtherTabsTimeoutMilliseconds);

      if (this.writeLogs) this.writeLog('[01-029] Started timeout to read other orion tabs');
    } else {
      if (this.writeLogs) this.writeLog('[01-030] LogOffLocal - True');

      const { currentPath } = this.getCurrentPath();
      if (currentPath !== '/' && currentPath !== '/home') {
        this.setRedirectUrl(currentPath);
      }
      this.redirectOnAuthentication(false);
    }

    if (this.writeLogs) this.writeLog('onConfigLoaded', data);
  }

  private onIdTokenExpired(data: unknown): void {
    if (this.checkIfRefreshingProcess()) {
      return;
    }
    this.writeLog('[01-010] onIdTokenExpired', data);
    this.refreshTokens();
  }

  private async onNewAuthorizationResult(data: AuthStateResult): Promise<void> {
    if (!data) {
      return;
    }

    if (data.isAuthenticated) {
      if (localStorage.getItem(EnumAuthFlags.IsLoggedOffLocal)) {
        return;
      }
      if (this.checkAuthTimeoutHandler) clearTimeout(this.checkAuthTimeoutHandler);
      this.sharedSessionStatus = 'Idle';

      // Post-authentication setup
      this.setRefreshSessionHandler();
      // Re-dispatch username in case this is an OIDC redirect callback (ngxsOnInit take(1) already consumed)
      this.store.dispatch(
        new LoginAdfsSuccess(
          await this.getUserName(),
          await this.getUpn(),
          await this.getPrimarySid(),
        ),
      );
      if (!this.initialSetupDone) {
        this.initialSetupDone = true;
        if (this.isFirstLogin()) {
          this.setLoginStatus(true);
          this.setFirstLoginFlag();
        }
      }

      // NOTE Gets the redirect URL from authentication service.
      // If no redirect has been set, uses the default.
      this.waitForPlantReady$().subscribe((): void => {
        if (this.getRedirectUrl()) {
          this.writeLog('[01-004] redirecting...');
          const redirectUrl = this.getRedirectUrl();
          // const path = Helper.getUrlPaths();
          serverErrorInterceptorState.unauthorizedErrorOccurred = false;
          this.removeRedirectUrl();
          if (this.router.url != redirectUrl) {
            this.router.navigateByUrl(redirectUrl ?? '/home');
          }
        } else {
          this.writeLog('[01-003] redirecting...');
          this.redirectOnAuthentication(true);
        }
      });

      setTimeout((): void => {
        sessionStorage.setItem(EnumAuthFlags.IsAuthFinished, this.CACHED_FLAG_VALUE);
        this.setSignalR();
        if (sessionStorage.length) {
          if (this.writeLogs) this.writeLog('[01-033] Sharing latest Data to Other tabs');
          this.sendLocalStorageEvent(EnumSharedSessionEvents.RenewToken, sessionStorage);
        }
      }, 200);
    } else {
      this.writeLog('[01-005] Unauthorized...');
      if (
        data.validationResult === ValidationResult.MaxOffsetExpired ||
        data.validationResult === ValidationResult.IncorrectAtHash //||
        //data.validationResult === ValidationResult.SecureTokenServerError   //neplatný token povede znovu na přihlašení ne na unauthorize
      ) {
        this.router.navigate(['/unauthorized']);
      } else {
        this.redirectOnUnAuthorize();
      }
    }
    if (this.writeLogs) this.writeLog('onNewAuthorizationResult', data);
  }

  private onTokenExpired(data: unknown): void {
    if (this.checkIfRefreshTokenExpired(this.getCurrentTime())) {
      return;
    }

    // vypršení tokenu by nesmí vynutit odhlášení, dokud je obnovovací token platnej.
    this.refreshTokens();
    // this.logoutAdfs();
    if (this.writeLogs) this.writeLog('onTokenExpired', data);
  }

  private onUserDataChanged(data: unknown): void {
    if (this.writeLogs) this.writeLog('onUserDataChanged', data);
  }

  ignoreUrlsForToken(reqUrl: string): boolean {
    return !this.ignoreUrls.find((url): boolean => reqUrl.indexOf(url) != -1);
  }

  private isFirstLogin(): boolean {
    return localStorage.getItem('isFirstLogin') !== 'false';
  }

  private setFirstLoginFlag(): void {
    localStorage.setItem('isFirstLogin', 'false');
  }

  private checkAuth(includingServer?: boolean): void {
    if (includingServer) {
      this.oidcSecurityService.checkAuthIncludingServer().subscribe({
        error: (err): void => {
          if (this.writeLogs) this.writeLog('checkAuthIncludingServer error', err);
        },
        next: (response): void => {
          this.sharedSessionStatus = 'Idle';

          if (response && response?.isAuthenticated) {
            this.setRefreshSessionHandler(); // setting up auto refresh bearer token handler
            this.store.dispatch(new SetUserRightsByRole());

            if (this.isFirstLogin()) {
              this.setLoginStatus(true);
              this.setFirstLoginFlag();
            }

            setTimeout((): void => {
              this.setSignalR();
              // share other tabs latest data
              if (sessionStorage.length) {
                if (this.writeLogs) this.writeLog('[01-033] Sharing latest Data to Other tabs');
                this.sendLocalStorageEvent(EnumSharedSessionEvents.RenewToken, sessionStorage);
              }
            }, 200);
          }

          if (this.writeLogs) {
            this.writeLog(
              'checkAuthIncludingServer ',
              `isAuthenticated ${response?.isAuthenticated}`,
            );
          }

          // NOTE ignore on first call
          if (!sessionStorage.getItem(EnumAuthFlags.IsAuthFinished)) {
            return;
          }

          // NOTE Handle Refresh navigation here
          this.writeLog('[01-002] redirecting...');
          this.redirectOnAuthentication(response?.isAuthenticated);
        },
      });
    } else {
      this.oidcSecurityService.checkAuth().subscribe({
        error: (err): void => {
          if (this.writeLogs) this.writeLog('checkAuth error', err);
        },
        next: (response): void => {
          if (this.writeLogs) {
            this.writeLog('checkAuth ', `isAuthenticated ${response?.isAuthenticated}`);
          }

          if (response?.isAuthenticated) {
            this.setRefreshSessionHandler();
            this.store.dispatch(new SetUserRightsByRole());

            setTimeout((): void => {
              this.setSignalR();
            }, 200);
          }

          // NOTE ignore on first call
          if (!sessionStorage.getItem(EnumAuthFlags.IsAuthFinished)) {
            return;
          }

          // NOTE Handle Refresh navigation
          this.writeLog('[01-001] redirecting...');
          this.redirectOnAuthentication(response?.isAuthenticated);
        },
      });
    }
  }

  private async setSignalR(): Promise<void> {
    let getIdToken = await firstValueFrom(this.getIdToken());
    if (this.signalRService.isAppHubConnected) {
      this.signalRService.reconnectSignalRService(getIdToken);
    } else {
      this.signalRService.initSignalRService(getIdToken);
    }
    this.signalRService.setTokenReady(true);
  }

  localStorageEvents(event: StorageEvent): void {
    if (!event?.newValue) return;

    switch (event.key) {
      case EnumSharedSessionEvents.Request:
        this.onSharedRequest(event.newValue);
        break;

      case EnumSharedSessionEvents.Reply:
        this.onSharedReply(event.newValue);
        break;

      case EnumSharedSessionEvents.RenewToken:
        this.onSharedRenewToken(event.newValue);
        break;

      case EnumSharedSessionEvents.Logout:
        this.onSharedLogout(event.newValue);
        break;

      default:
        break;
    }
  }

  //#region Shared Events Implementation
  private sendLocalStorageEvent(eventName: string, data?: TabPayload): void {
    const tabData: SessionExchangeFormat = {
      tabId: this.tabId,
      tabPayload: data,
    };
    localStorage.setItem(eventName, JSON.stringify(tabData));
    setTimeout((): void => {
      localStorage.removeItem(eventName);
    }, 200);
  }

  formatTabData(tabData: string): null | SessionExchangeFormat {
    try {
      return JSON.parse(tabData) as SessionExchangeFormat;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  private onSharedRequest(sharedData: string): void {
    const tabData = this.formatTabData(sharedData);

    if (!tabData) {
      return;
    }

    if (this.writeLogs) {
      this.writeLog('SharedSessionEvents.Request');
    }

    // Condition on which tab should not reply
    if (
      tabData.tabId === this.tabId ||
      localStorage.getItem(EnumAuthFlags.IsLoggedOffLocal) ||
      this.isAuthenticated() === false
    ) {
      return;
    }

    this.sendLocalStorageEvent(EnumSharedSessionEvents.Reply, sessionStorage);
  }

  private onSharedReply(sharedData: string): void {
    if (this.checkAuthTimeoutHandler) {
      clearTimeout(this.checkAuthTimeoutHandler);
    }

    const tabData: null | SessionExchangeFormat = this.formatTabData(sharedData);
    if (!tabData) return;

    if (this.sharedSessionStatus === 'Idle' || tabData.tabId === this.tabId) {
      return;
    }

    this.sharedSessionStatus = 'Idle';

    const data: TabPayload | undefined = tabData.tabPayload;
    console.warn('[Shared Session Reply] received data from other tab', data);

    // remove incompatible fields between tabs
    if (data) {
      if (data?.redirectUrl) delete data['redirectUrl'];
    }

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const value = data[key];
        if (value !== undefined) {
          sessionStorage.setItem(key, value);
        }
      }
    }

    const currentTimeMillis = this.getCurrentTime();

    setTimeout(async (): Promise<void> => {
      if (currentTimeMillis > (await this.calculateRenewTokenExpireTimeInMS())) {
        // Received Expired Refresh Token From Other Tab
        this.writeLog('[01-019] Logging out....');
        this.logoutAdfs();
      } else if (currentTimeMillis < ((await this.getIdTokenExpireTime()) ?? 0)) {
        // Received Expired Id Token From Other Tab
        this.writeLog('[01-020] Authenticating....');
        this.checkAuth(true);
      } else {
        // Received Data From Other Tab Successfully
        this.checkAuth();
      }
    }, 200);

    if (this.writeLogs) this.writeLog('SharedSessionEvents.Reply');
  }

  private async onSharedRenewToken(sharedData: string): Promise<void> {
    this.resetRefreshSessionVars();

    const tabData: null | SessionExchangeFormat = this.formatTabData(sharedData);

    if (tabData?.tabId === this.tabId) {
      return;
    }

    const data = tabData?.tabPayload as Record<string, string>;

    if (data?.['redirectUrl']) {
      delete data?.['redirectUrl'];
    }

    Object.entries(data).forEach(([key, value]): void => {
      sessionStorage.setItem(key, value);
    });

    setTimeout(async (): Promise<void> => {
      this.setSignalR();
      this.store.dispatch(new SetUserRightsByRole());
      await this.setRefreshSessionHandler();
    }, 200);

    if (this.writeLogs) {
      this.writeLog('SharedSessionEvents.RenewToken');
    }
  }

  private async onSharedLogout(sharedData: string): Promise<void> {
    const tabData: null | SessionExchangeFormat = this.formatTabData(sharedData);

    if (tabData?.tabId === this.tabId) {
      return;
    }

    if (this.writeLogs) {
      this.writeLog('SharedSessionEvents.Logout');
    }

    if (this.refreshSessionHandler) {
      clearTimeout(this.refreshSessionHandler);
    }

    if (this.expiredSessionHandler) {
      clearTimeout(this.expiredSessionHandler);
    }

    this.idToken = await firstValueFrom(this.getIdToken());
    console.log('[Dialog Testing] logout calling from onSharedLogout');
    this.logoutHandler();
  }

  logoutHandler(): void {
    console.log('[Dialog Testing] logout Session!');
    // Use the token captured before sessionStorage was cleared
    const idToken = this.idToken;
    this.oidcSecurityService.logoffLocal();

    // call LogOut API
    window.location.href =
      'https://auth.prod.orion.cz.foxconn.com/realms/OrionProd/protocol/openid-connect/logout?id_token_hint=' +
      idToken +
      '&post_logout_redirect_uri=' +
      encodeURIComponent(window.location.origin + '/login');
    this.router.navigate(['/home']);
  }

  redirectOnAuthentication(isAuthenticated: boolean): void {
    if (this.writeLogs) {
      this.writeLog('redirectOnAuthentication');
    }

    if (!isAuthenticated) {
      //TODO save url
      this.router.navigate(['/home']);
      return;
    }

    this.waitForPlantReady$().subscribe((): void => {
      const redirectUrl = this.getRedirectUrl();
      const { currentPath, directoryPath } = this.getCurrentPath();

      if (redirectUrl) {
        this.removeRedirectUrl();

        if (this.router.url !== redirectUrl) {
          this.router.navigateByUrl(redirectUrl);
        }
      } else {
        if (directoryPath && directoryPath !== '/home') {
          this.router.navigateByUrl(currentPath);
        } else {
          this.router.navigate(['/dashboard']);
        }
      }
    });
  }

  /**
   * Emits once when `activePlant` is populated in the store (after LoadConfig finishes),
   * or after PLANT_WAIT_TIMEOUT_MS so the user is not stuck indefinitely if plants fail to load.
   * Used to gate post-login redirect so downstream components can assume a plant exists.
   */
  waitForPlantReady$(): Observable<unknown> {
    // Inline state selector avoids importing AuthenticationState (would cause circular dep).
    return this.store
      .select<string>((state): string => {
        const auth = state && state.authentication;
        if (!auth) return '';
        const p = auth.plant;
        return typeof p === 'object' && p ? p.Code : p || '';
      })
      .pipe(
        filter((plant): plant is string => !!plant),
        take(1),
        timeout(PLANT_WAIT_TIMEOUT_MS),
        catchError((): Observable<string> => of('')),
        map((): void => undefined),
      );
  }

  private redirectOnUnAuthorize(): void {
    if (this.writeLogs) this.writeLog('redirectOnUnAuthorize');

    try {
      this.oidcSecurityService.isAuthenticated$.subscribe({
        error: (err): void => {
          Helper.printError(err, '[01-002] redirectOnUnAuthorize');
          this.router.navigateByUrl('/home');
        },
        next: (authenticatedResult: AuthenticatedResult): void => {
          if (authenticatedResult?.isAuthenticated) {
            this.logoutAdfs();
          } else {
            this.router.navigateByUrl('/home');
          }
        },
      });
    } catch (e) {
      Helper.printError(e, '[01-001] redirectOnUnAuthorize');
      this.router.navigateByUrl('/home');
    }
  }

  getCurrentPath(): { currentPath: string; directoryPath: string } {
    const parsedUrl = new URL(window.location.href);
    const path = `${parsedUrl.pathname}${parsedUrl.search}`;
    return { currentPath: path, directoryPath: parsedUrl.pathname };
  }

  private writeLog(key: string, data?: string | unknown): void {
    console.log(key);
    if (data) {
      console.log(data);
    }
  }

  private async calculateIdTokenExpireTimeInSeconds(): Promise<number | undefined> {
    const currentTimeMillis = new Date().getTime();
    let expireTimeInMillis = await this.getIdTokenExpireTime();
    let result;
    if (expireTimeInMillis) {
      const formattedOffset: number = this.tokenExpireOffsetInSeconds * 1000;
      expireTimeInMillis = expireTimeInMillis - formattedOffset;
      result = (expireTimeInMillis - currentTimeMillis) / 1000;
      result = result > 0 ? result : 0;
      if (this.writeLogs) this.writeLog(`Token Expires In ${result} Seconds`);
    }
    return result;
  }

  private async calculateRenewTokenExpireTimeInMS(): Promise<number> {
    const authTime = await this.getAuthTime();
    return authTime !== undefined ? (authTime + 82800) * 1000 : Date.now();
  }

  // Retrieve the auth result from sessionStorage
  private getAuthResult(): null | TokenRefreshResponse {
    const authResultKey = this.findAuthResultKey();
    if (!authResultKey) {
      console.log('[session dialog] No auth result key found in session storage.');
      return null;
    }

    const authResult = sessionStorage.getItem(authResultKey);
    console.log('[session dialog] calling getAuthResult....');
    return authResult ? (JSON.parse(authResult) as TokenRefreshResponse) : null;
  }

  // Find the key that ends with _authnResult
  private findAuthResultKey(): null | string {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.endsWith(this.authResultKeySuffix)) {
        return key;
      }
    }
    return null;
  }

  // Get the refresh token expired In
  private getRefreshTokenExpirationIn(): number | undefined {
    const authResult = this.getAuthResult();
    console.log('[session dialog] get authResult :', authResult);
    if (authResult && typeof authResult === 'object' && 'refresh_expires_in' in authResult) {
      // The 'refresh_expires_in' is in seconds
      const value = (authResult as { refresh_expires_in: number }).refresh_expires_in;
      console.log('[session dialog] refresh token expired in :', value);

      if (typeof value === 'number') {
        return value;
      }
    }
    return undefined;
  }

  async setRefreshSessionHandler(immediate: boolean = false): Promise<void> {
    if (this.checkIfRefreshingProcess()) {
      // If this is a retry (immediate=true) and no refresh is in progress,
      // the session is unrecoverably broken.
      if (immediate && !this.isRefreshingSession) {
        this.writeLog(
          '[01-034] setRefreshSessionHandler - Session broken (IsAuthFinished missing on retry), logging out',
        );
        this.logoutAdfs();
      } else {
        this.writeLogs
          ? this.writeLog(
              '[01-034] setRefreshSessionHandler - Ignoring Refresh Process - Already Process Found',
            )
          : null;
      }
    }

    if (this.refreshSessionHandler) clearTimeout(this.refreshSessionHandler);
    if (this.expiredSessionHandler) clearTimeout(this.expiredSessionHandler);

    let expirationTimeInSeconds: number | undefined;
    let refreshTokenExpirationTimeInSeconds: number | undefined;

    try {
      expirationTimeInSeconds = await this.calculateIdTokenExpireTimeInSeconds(); // Bearer Token
    } catch (e) {
      if (this.writeLogs) this.writeLog('[01-035] Error calculateIdTokenExpireTimeInSeconds', e);
    }

    try {
      this.renewTokenExpireTimeInMilliseconds = await this.calculateRenewTokenExpireTimeInMS(); // Refresh Token
    } catch (e) {
      if (this.writeLogs) this.writeLog('[01-036] Error calculateRenewTokenExpireTimeInMS', e);
    }

    try {
      refreshTokenExpirationTimeInSeconds = this.getRefreshTokenExpirationIn(); // Refresh Token Expired In
    } catch (e) {
      if (this.writeLogs) this.writeLog('[01-036] Error getRefreshTokenExpirationIn', e);
    }

    // ORB-6875: schedule the silent refresh on the SHORTEST live window, not just the access token.
    // Keycloak returns refresh_expires_in tracking SSO Session Idle (~30 min on OrionProd), which is
    // far shorter than the 8h access token. If we waited for the access token to expire the session
    // would idle out at ~30 min. Refreshing on the idle window (with a safety margin for background
    // -tab timer throttling) resets that window on every cycle, so the session keeps running
    // continuously up to Keycloak SSO Session Max (10h), after which reAuthenticate() takes over.
    const REFRESH_SAFETY_SECONDS = 120;
    let nextRefreshInSeconds = expirationTimeInSeconds;
    if (refreshTokenExpirationTimeInSeconds && !isNaN(refreshTokenExpirationTimeInSeconds)) {
      const refreshWindowInSeconds = Math.max(
        refreshTokenExpirationTimeInSeconds - REFRESH_SAFETY_SECONDS,
        5,
      );
      nextRefreshInSeconds =
        !expirationTimeInSeconds || isNaN(expirationTimeInSeconds)
          ? refreshWindowInSeconds
          : Math.min(expirationTimeInSeconds, refreshWindowInSeconds);
    }
    const refreshDelayInSeconds = immediate ? 5 : nextRefreshInSeconds;

    if (refreshDelayInSeconds) {
      this.refreshSessionHandler = setTimeout((): void => {
        if (this.checkIfRefreshingProcess()) {
          if (this.writeLogs)
            this.writeLog('[01-037] Ignoring Refresh Process - Already Process Found');
          return;
        }

        if (this.checkIfRefreshTokenExpired(this.getCurrentTime())) {
          this.writeLog('[01-016] Refresh token expired — re-authenticating silently');
          return;
        }

        // ORB-6875: no inactivity dialog. Refresh the access token silently in the background so
        // the session keeps running with no user interaction. refreshTokens() re-arms this handler
        // for the next cycle and falls back to silent re-login if the refresh token is dead.
        this.writeLog('[01-013] Access token expiring — refreshing silently');
        this.refreshTokens();
      }, refreshDelayInSeconds * 1000);
    } else {
      if (this.writeLogs) this.writeLog('expirationTimeInSeconds', 'isNaN');
    }

    if (refreshTokenExpirationTimeInSeconds) {
      this.expiredSessionHandler = setTimeout((): void => {
        // ORB-6875: refresh token has reached its hard limit (Keycloak SSO Session Max / dead after
        // long sleep). No "session expired" dialog — re-authenticate silently. If the Keycloak SSO
        // cookie is still alive this is seamless; otherwise the user lands on the login page.
        this.writeLog('Refresh token expired — re-authenticating silently');
        this.reAuthenticate();
      }, refreshTokenExpirationTimeInSeconds * 1000);
    }
  }

  /**
   * ORB-6875: silent re-authentication used when the access/refresh token can no longer be renewed.
   * Stores the current path and triggers the OIDC authorize flow — seamless if the Keycloak SSO
   * session is still valid, otherwise it surfaces the login page. Replaces the old idle/expiry
   * dialogs and forced logouts.
   */
  private reAuthenticate(): void {
    if (this.isRefreshingSession) {
      return;
    }
    const { currentPath } = this.getCurrentPath();
    this.setRedirectUrl(currentPath);
    this.oidcSecurityService.authorize();
  }

  private checkIfRefreshingProcess(): boolean {
    if (this.isRefreshingSession) {
      if (this.writeLogs) {
        this.writeLog('[01-039] checkIfRefreshingProcess - Refreshing Session');
      }
      return true;
    }

    const isAuthFinished = sessionStorage.getItem(EnumAuthFlags.IsAuthFinished);
    if (!isAuthFinished) {
      if (this.writeLogs) {
        this.writeLog(
          '[01-040] checkIfRefreshingProcess - EnumAuthFlags.IsAuthFinished Not Present In Session',
        );
      }
      return true;
    }

    return false;
  }

  private resetRefreshSessionVars(): void {
    if (this.writeLogs) {
      this.writeLog('[01-41] Started resetRefreshSessionVars');
    }
    this.isRefreshingSession = false;
    if (this.writeLogs) {
      this.writeLog('[01-42] Ended resetRefreshSessionVars');
    }
  }

  checkIfRefreshTokenExpired(currentTimeMillis: number): boolean {
    const renewExpire = this.renewTokenExpireTimeInMilliseconds;
    if (renewExpire < currentTimeMillis) {
      this.logSessionRefreshEvent('forced-logout-guard-fired', {
        diffSeconds: isNaN(renewExpire)
          ? null
          : Math.round((renewExpire - currentTimeMillis) / 1000),
        now: new Date(currentTimeMillis).toISOString(),
        renewExpire: isNaN(renewExpire) ? 'NaN' : new Date(renewExpire).toISOString(),
      });
      // ORB-6875: refresh token expired — silently re-authenticate instead of forced logout.
      this.reAuthenticate();
      return true;
    } else {
      return false;
    }
  }

  getCurrentTime(): number {
    return new Date().getTime();
  }

  private localStorageListener(): void {
    // listen for changes to localStorage
    try {
      if (window.addEventListener) {
        window.addEventListener('storage', (event): void => this.localStorageEvents(event), false);
        if (this.writeLogs) {
          this.writeLog('[01-027]setting window storage localstorage listener');
        }
      }
    } catch (e) {
      console.error(e);
      if (this.writeLogs) {
        this.writeLog('[01-026] Error in setting localstorage listener');
      }
    }
  }
}
